import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiWithoutCamelization } from "../../../api";
import { useOnSiteStructureChanged } from "../../../hooks/useOnSiteStructureChanged";
import urls from "../../../urls";
import { COMPONENT_QUERY_KEY } from "../../../utils/constants";
import { ObjectName } from "../../../utils/enums";
import type {
  GasConnectionCreateRequest,
  GasConnectionResponse,
  GasConnectionUpdateRequest
} from "../GasConnection.types";
import { gasConnectionQueries } from "./useGasConnection";

export function useGasConnectionMutation(siteId: number) {
  const queryClient = useQueryClient();
  const cacheName = COMPONENT_QUERY_KEY[ObjectName.GasConnection];
  const onSiteStructureChanged = useOnSiteStructureChanged(siteId);

  const createMutation = useMutation({
    mutationFn: (createRequest: GasConnectionCreateRequest) =>
      createGasConnection(createRequest, siteId),
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: [cacheName, { siteOrVariantId: siteId }]
      });
      onSiteStructureChanged();
    }
  });

  const editMutation = useMutation({
    mutationFn: (updatedData: GasConnectionUpdateRequest) =>
      editGasConnection(updatedData),
    onSuccess(response) {
      queryClient.invalidateQueries({
        queryKey: [cacheName, { siteOrVariantId: siteId }]
      });
      queryClient.invalidateQueries({
        queryKey: gasConnectionQueries.detail(response.data.id).queryKey
      });
      onSiteStructureChanged();
    }
  });

  return {
    createMutation,
    editMutation
  };
}

function createGasConnection(data: GasConnectionCreateRequest, siteId: number) {
  const url = urls.apiWithoutCamelization.gasConnections(siteId);
  return apiWithoutCamelization.post<GasConnectionResponse>(url, data);
}

function editGasConnection(data: GasConnectionUpdateRequest) {
  const url = urls.apiWithoutCamelization.gasConnection(data.id);
  return apiWithoutCamelization.patch<GasConnectionResponse>(url, data);
}
